/** biome-ignore-all lint/a11y/useSemanticElements: <explanation> */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: <explanation> */
import type React from 'react';
import { useState } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import '../styles/LoginForm.css';
import AuthorizationCard from './AuthorizationCard';
import TwoFAVerification from './TwoFAVerification';

type LoginFormProps = {
  onLogin: (data: { email: string; password: string }) => void;
  onGoogleLogin?: (credential: any) => void;
  onVerify: (code: string) => void;
  onResend: () => void;
  redirectLogin: () => void;

  onAuthorizeApp: () => void;
  isLoading?: boolean;
  googleClientId?: string;
  enableGoogleLogin?: boolean;
  appName?: string;
  LoggedIn?: boolean;
  visibility2FA: boolean;
  rememberLogin: (rememberMe: boolean) => void;
  loginError?: string | null;
  twoFAError?: string | null;
};

const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  onGoogleLogin,
  onAuthorizeApp,
  onVerify,
  onResend,
  redirectLogin,
  isLoading = false,
  googleClientId,
  enableGoogleLogin = false,
  appName,
  LoggedIn = false,
  visibility2FA = false,
  rememberLogin,
  loginError,
  twoFAError,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleCheckboxChange = () => {
    setRememberMe((rememberLogin) => !rememberLogin);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    onLogin({ email, password });
    rememberLogin(rememberMe);
  };

  if (LoggedIn && !authorized) {
    return (
      <div
        style={{
          marginLeft: '-15px',
          width: '100%',
        }}
      >
        <AuthorizationCard
          appName={appName}
          isLoading={isLoading}
            onAuthorize={() => {
            onAuthorizeApp();
            setAuthorized(true);
          }}
        />
      </div>
    );
  }

  if (authorized) {
    return (
      <div
      id="au"
        className="wrapper"
        style={{ textAlign: 'center' }}
      >
        <h2 className="successTitle">{loginError ? 'Authorization Failed' : 'Authorization in Progress'}</h2>

        {loginError && (
          <div
            className="login-error"
            style={{
              color: 'red',
              marginBottom: 20,
              textAlign: 'center',
              padding: '12px',
              backgroundColor: '#fff5f5',
              border: '1px solid #fed7d7',
              borderRadius: '4px',
              maxWidth: '300px',
              margin: '0 auto 20px auto',
            }}
          >
            <strong>Authorization Error:</strong>
            <br />
            {loginError}
          </div>
        )}

        {!loginError && (
          <>
            <div
              style={{
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid rgba(0,0,0,.1)',
                borderRadius: '50%',
                borderTopColor: '#09f',
                animation: 'spin 1s linear infinite',
                margin: '20px auto',
              }}
            ></div>
            <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
          </>
        )}
      </div>
    );
  }

  if (visibility2FA) {
    return (
      <TwoFAVerification
        onVerify={onVerify}
        onResend={onResend}
        twoFAError={twoFAError}
        onCantLogin={redirectLogin}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className="wrapper">
      <div className="card">
        <h2 className="title">Login</h2>

        {loginError && (
          <div
            className="login-error"
            style={{ color: 'red', marginBottom: 12, textAlign: 'center' }}
          >
            {loginError}
          </div>
        )}

        {enableGoogleLogin && googleClientId && (
          <>
            <div
              className="googleBtnWrapper"
              onClick={onGoogleLogin}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.01)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <div className="googleLogoPart">
                <FcGoogle size={20} />
              </div>
              <div className="googleTextPart">{isLoading ? 'Signing in...' : 'Continue with Google'}</div>
            </div>

            <div className="divider">
              <hr className="hr" />
              <span className="dividerText">or</span>
              <hr className="hr" />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="inputGroup">
            <label
              htmlFor="email"
              className="label"
            >
              Email
            </label>
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={isLoading}
              required
            />
          </div>

          <div className="inputGroup">
            <label
              htmlFor="password"
              className="label"
            >
              Password
            </label>
            <div className="passwordWrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={isLoading}
                required
              />
              <span
                className="eyeIcon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>
          </div>

          <div className="checkboxGroup">
            <input
              checked={rememberMe}
              onChange={handleCheckboxChange}
              type="checkbox"
              id="remember"
              disabled={isLoading}
            />
            <label htmlFor="remember">Remember me</label>
          </div>

          <button
            type="submit"
            className={`loginBtn ${isLoading ? 'loginBtnDisabled' : ''}`}
            disabled={isLoading}
          >
            {isLoading && <span className="loadingSpinner"></span>}
            {isLoading ? 'Logging in...' : 'Log in'}
          </button>
        </form>

        <div className="footer">
          <a
            href="https://app.elasticemail.com/forgot"
            target="_blank"
            className="footerLink"
            rel="noopener noreferrer"
          >
            Forgot Password
          </a>
          <a
            href="https://elasticemail.com/select-product"
            target="_blank"
            className="footerLink"
            rel="noopener noreferrer"
          >
            Sign up
          </a>
        </div>
      </div>
    </div>
  );
};

declare global {
  interface Window {
    google: any;
  }
}

export default LoginForm;
